import { type Signal, type ReadonlySignal } from '@preact/signals'
import { Role, type Collaborator } from 'core'
import { Link } from 'wouter-preact'

import { ChevronLeftIcon, HorizontalDivider, Typography } from 'components/UI'

import { AddCollaboratorModal } from './AddCollaboratorModal'
import { CollaboratorsTable } from './CollaboratorsTable'
import { CollaboratorsTitleSection } from './CollaboratorsTitleSection'
import { NoCollaboratorsPlaceholder } from './NoCollaboratorsPlaceholder'
import { RemoveCollaboratorModal } from './RemoveCollaboratorModal'

interface Props {
	addCollaborator: () => void
	addOtherCollaborator: () => void
	wasAddCollaboratorSuccess: boolean
	closeAddCollaboratorModal: () => void
	collaboratorEmail: Signal<string>
	collaborators: Collaborator[]
	collaboratorUsernameToRemove: Signal<string>
	errorMessageForCreatingCollaboratorModal?: string
	errorMessageForRemoveCollaboratorModal?: string
	inputErrors: ReadonlySignal<Record<string, string | undefined>>
	isAddCollaboratorButtonDisabled: boolean
	isAddCollaboratorModalOpen: boolean
	isRemoveCollaboratorButtonDisabled: boolean
	isRemoveCollaboratorButtonLoading: boolean
	isRemoveCollaboratorModalOpen: boolean
	isUserCollaborator: boolean
	onAddCollaboratorModal: () => void
	onCloseRemoveCollaboratorModal: () => void
	openRemoveCollaboratorModal: (collaboratorUsername: string) => void
	projectName: string
	removeCollaborator: () => void
	rolesState: ReadonlySignal<Role[]>
	rolSelectedState: ReadonlySignal<string>
	setCollaboratorRole: (_: string, value: string) => void
	updateCollaboratorEmail: (value: string) => void
}

const SettingsTemplate = ({
	addCollaborator,
	addOtherCollaborator,
	closeAddCollaboratorModal,
	collaboratorEmail,
	collaborators,
	collaboratorUsernameToRemove,
	errorMessageForCreatingCollaboratorModal,
	errorMessageForRemoveCollaboratorModal,
	inputErrors,
	isAddCollaboratorButtonDisabled,
	isAddCollaboratorModalOpen,
	isRemoveCollaboratorButtonDisabled,
	isRemoveCollaboratorButtonLoading,
	isRemoveCollaboratorModalOpen,
	isUserCollaborator,
	onAddCollaboratorModal,
	onCloseRemoveCollaboratorModal,
	openRemoveCollaboratorModal,
	projectName,
	removeCollaborator,
	rolesState,
	rolSelectedState,
	setCollaboratorRole,
	updateCollaboratorEmail,
	wasAddCollaboratorSuccess
}: Props) => (
	<>
		<div class='m-auto mb-14 max-w-[1060px] px-8 py-8 xl:px-3 2xl:px-0'>
			<Link
				class='mb-1 flex items-center gap-2 text-black-400 hover:text-product'
				href='/'
			>
				<ChevronLeftIcon />
				<Typography
					class='mt-[1px]'
					color='text-black-400'
					text='Back to Dashboard'
					variant='text3'
				/>
			</Link>

			<Typography
				class='mb-2.5 pr-5'
				color='text-product'
				text='General Settings'
				variant='h2'
			/>
			<Typography
				color='text-black-200'
				text={
					isUserCollaborator
						? 'Project Information'
						: 'Manage your overall settings and preferences here.'
				}
				variant='text2'
			/>

			<HorizontalDivider className='my-10' />

			<CollaboratorsTitleSection
				hasCollaborators={collaborators.length !== 0}
				isUserCollaborator={isUserCollaborator}
				onOpenAddCollaboratorModal={onAddCollaboratorModal}
			/>

			{collaborators.length !== 0 ? (
				<CollaboratorsTable
					collaborators={collaborators}
					isUserCollaborator={isUserCollaborator}
					openRemoveCollaboratorModal={openRemoveCollaboratorModal}
				/>
			) : (
				<NoCollaboratorsPlaceholder
					isUserCollaborator={isUserCollaborator}
					onOpenAddCollaboratorModal={onAddCollaboratorModal}
				/>
			)}
		</div>

		<AddCollaboratorModal
			addCollaborator={addCollaborator}
			addOtherCollaborator={addOtherCollaborator}
			bannerErrorMessage={errorMessageForCreatingCollaboratorModal}
			closeAddCollaboratorModal={closeAddCollaboratorModal}
			collaboratorEmail={collaboratorEmail}
			displaySuccessContent={wasAddCollaboratorSuccess}
			inputErrors={inputErrors}
			isAddCollaboratorButtonDisabled={isAddCollaboratorButtonDisabled}
			isModalOpen={!isUserCollaborator && isAddCollaboratorModalOpen}
			projectName={projectName}
			rolesState={rolesState}
			rolSelectedState={rolSelectedState}
			selectCollaboratorRole={setCollaboratorRole}
			updateCollaboratorEmail={updateCollaboratorEmail}
		/>

		<RemoveCollaboratorModal
			bannerErrorMessage={errorMessageForRemoveCollaboratorModal}
			collaboratorUsername={collaboratorUsernameToRemove}
			isModalOpen={!isUserCollaborator && isRemoveCollaboratorModalOpen}
			isRemoveCollaboratorButtonDisabled={isRemoveCollaboratorButtonDisabled}
			isRemoveCollaboratorButtonLoading={isRemoveCollaboratorButtonLoading}
			onCloseRemoveCollaboratorModal={onCloseRemoveCollaboratorModal}
			projectName={projectName}
			removeCollaborator={removeCollaborator}
		/>
	</>
)

export { SettingsTemplate }
